/* bl-attr.js — first-touch source, written onto the Shopify cart as _bl_ref.
   Added 20 Sep 2026.

   WHY THIS EXISTS
   Organic search revenue could not be measured. Orders carry a UTM source when they come from an
   ad, so Meta and Google Ads are attributed fine — but a visitor who arrives from an ordinary
   Google search carries no UTM at all, and lands in the same bucket as someone who typed the
   address in. That bucket was 426 orders and about Rs 12 lakh, and there was no way to tell how
   much of it SEO had earned.
   The referrer WAS available in the CDP's session table, but only 30-40% of orders carry a
   session id to join on. Meanwhile the two scripts in theme.liquid that write cart attributes —
   the A/B variant writer and the ChatGPT UTM writer — reach 96% of orders. So the fix is not to
   repair the join. It is to capture the source the same way those two do.

   HOW IT WORKS, AND WHY EACH PART IS THERE
   - FIRST TOUCH, NOT LAST. document.referrer is only correct on the page the visitor arrives on.
     By the time they add to cart they have clicked around and the referrer is our own domain. So
     the source is worked out on arrival and kept in sessionStorage for the rest of the visit.
   - GCLID BEATS THE REFERRER. A Google Ads click also arrives referred by google.com. If the URL
     carries gclid (or gad_source / gbraid / wbraid), it is PAID, whatever the referrer says.
     Getting this backwards would credit paid clicks to SEO, which is worse than measuring
     nothing.
   - OUR OWN DOMAIN IS NOT A SOURCE. An internal referrer means they were already here; the
     stored first touch must survive it rather than be overwritten with "direct".
   - SELF-HEALING, NOT FIRE-AND-FORGET. The attribute can only be written once the cart exists,
     and the cart may be created before or after this script runs. So it checks the live cart and
     only POSTs when the value actually differs — the same approach as the A/B writer, which is
     what gets that one to 96%.
   - UNDERSCORE PREFIX hides the attribute from the cart and checkout UI, exactly as _ab_pdp_variant
     and _ab_cart_variant are hidden. The customer never sees it.
   - EVERYTHING IS WRAPPED. This runs on every page of a live store. It must never be able to
     break a page, so every storage read and network call is guarded; storage throws outright in
     some in-app browsers, and 47% of this traffic is in-app.

   WHAT IT DELIBERATELY DOES NOT DO
   - No cookies, no identifiers, no personal data. The value is a source label such as
     "google-organic" or "direct". Nothing about the person.
   - It does not touch line items, prices, discounts or anything the customer can see.
   - It only ever sets ONE attribute. If it is ever wrong, one field is wrong.
*/
(function () {
  try {
    var ATTR = '_bl_ref';
    var SKEY = 'bl_first_ref';

    function classify() {
      var qs, ref = '';
      try { qs = new URLSearchParams(location.search); } catch (e) { qs = null; }
      var get = function (k) { try { return qs ? (qs.get(k) || '') : ''; } catch (e) { return ''; } };

      /* Paid click identifiers win over the referrer. A Google Ads visit is referred by
         google.com too, and crediting that to organic would flatter SEO with ad spend. */
      if (get('gclid') || get('gad_source') || get('gbraid') || get('wbraid')) return 'google-paid';
      if (get('fbclid')) return 'meta-paid';

      /* A UTM source means some campaign already owns this visit; say so plainly rather than
         guessing from the referrer, and let the existing utm_source field carry the detail. */
      var us = (get('utm_source') || '').toLowerCase();
      if (us) return 'utm:' + us.slice(0, 40);

      try { ref = document.referrer || ''; } catch (e) { ref = ''; }
      if (!ref) return 'direct';

      var host = '';
      try { host = new URL(ref).hostname.toLowerCase(); } catch (e) { return 'other'; }

      /* Our own pages are not a source — return empty so a stored first touch is kept. */
      if (host.indexOf('basicslife') !== -1) return '';

      if (/(^|\.)google\./.test(host)) return 'google-organic';
      if (/(^|\.)(bing|duckduckgo|yahoo|ecosia|search\.brave)\./.test(host)) return 'search-organic';
      if (/(chatgpt|openai|perplexity|gemini|claude|copilot)\./.test(host)) return 'ai:' + host.slice(0, 40);
      if (/(facebook|instagram|fb\.|fbcdn|threads)\./.test(host)) return 'meta-organic';
      if (/(youtube|whatsapp|t\.co|twitter|x\.com|linkedin|pinterest)\./.test(host)) return 'social:' + host.slice(0, 30);
      return 'ref:' + host.slice(0, 40);
    }

    function firstTouch() {
      var stored = '';
      try { stored = sessionStorage.getItem(SKEY) || ''; } catch (e) { stored = ''; }
      if (stored) return stored;
      var now = classify();
      /* Empty means an internal referrer with nothing stored yet — the visit began somewhere we
         did not see, so record it as direct rather than leaving it blank. */
      if (!now) now = 'direct';
      try { sessionStorage.setItem(SKEY, now); } catch (e) { /* private mode; carry on */ }
      return now;
    }

    var want = firstTouch();
    if (!want) return;

    function sync(cart) {
      /* No cart, no attributes to hold — this is normal for most page views and is not a failure. */
      if (!cart || !cart.item_count) return;
      var cur = (cart.attributes && cart.attributes[ATTR]) || '';
      if (String(cur) === String(want)) return;
      var body = {}; body[ATTR] = want;
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: body })
      }).catch(function () { /* a failed write retries on the next cart event */ });
    }

    function check() {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(sync)
        .catch(function () { });
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', check);
    else check();

    /* The cart is usually created after this script has already run, so watch for it. */
    ['cart:updated', 'cart:refresh', 'cart:change', 'cart:added', 'cart:build'].forEach(function (ev) {
      document.addEventListener(ev, function () { setTimeout(check, 60); });
    });

    /* Shopify's own add/change calls do not always emit those events, so hook fetch as the A/B
       writer does. Guarded by a flag so repeated script loads cannot stack wrappers. */
    if (window.fetch && !window.__blAttrHook) {
      window.__blAttrHook = 1;
      var _f = window.fetch;
      window.fetch = function () {
        var u = arguments[0];
        u = (u && u.url) ? u.url : String(u || '');
        var pr = _f.apply(this, arguments);
        try {
          if (/\/cart\/(add|change|clear)(\.js)?/i.test(u)) {
            pr.then(function () { setTimeout(check, 150); }).catch(function () { });
          }
        } catch (e) { }
        return pr;
      };
    }
  } catch (e) { /* never break a page over analytics */ }
})();
